import React, {ChangeEvent, Fragment, memo, useState} from "react";
import Button from "@mui/material/Button";
import {SelectChangeEvent} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import stepmods from "../../_helper/stepmods";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import DeleteIcon from '@mui/icons-material/Delete';
import {displayMod} from "../../_helper/displayMod";
import {s_char, s_char_mods} from "../../_signals/s_char";
import {ICharMod} from "../../_interfaces/ICharMod";
import EditIcon from "@mui/icons-material/Edit";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import {LockReset} from "@mui/icons-material";
import ListSubheader from "@mui/material/ListSubheader";
import {ModsAdd, ModsDelete, ModsUpdate} from "../../_api/BEMods";

interface IProps {
    mod: ICharMod
    mods: number
    mnum: number
}

function ModEdit({mod, mods, mnum}: IProps) {

    const [editedMod, setEditedMod] = useState<ICharMod | null>(null);
    const [isEditing, setIsEditing] = useState(false); // Neuer Zustand für den Bearbeitungsmodus

    const duration = [...Array(31).keys()];

    interface modType {
        key: string,
        name: string,
        dur: boolean
    }

    const modsTypes: modType[] = [
        {key: 'constant', name: 'dauernd', dur: false},
        {key: 'rolls', name: 'Würfe', dur: true},
        {key: 'rounds', name: 'Runden', dur: true},
        {key: 'minutes', name: 'Minuten', dur: true},
        {key: 'hours', name: 'Stunden', dur: true},
    ]

    const modTitles = {
        'kv': 'Körperliche Verteidigung',
        'mv': 'Magische Verteidigung',
        'wund': 'Wundschwelle',
        'pr': 'Physische Rüstung',
        'mr': 'Mystische Rüstung',
        'dmg': 'Schadenswurf',
        'hit': 'Trefferwurf'
    }

    const edit = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> | SelectChangeEvent<number | string>) => {
        const field = event.target.name;
        const value = event.target.value;

        let edited = editedMod;
        if (edited) {
            setEditedMod(prevEditedMod => {
                if (prevEditedMod) {
                    return { ...prevEditedMod, [field]: value };
                }
                return null;
            });
        }
    }


    const remove = (id) => {
        ModsDelete(s_char.value.id,[id])
    }

    const modDuration = (type: string) => {
        const modType = modsTypes.find(mT => mT.key === type);
        return modType ? modType.dur : false;
    }

    const startEditing = (mod: ICharMod) => {
        setEditedMod(mod);
        setIsEditing(true)
    };

    const saveChanges = () => {
        console.log('saveChanges', editedMod)
        if (editedMod) {
            let mod = editedMod;

            s_char_mods.value = s_char_mods.value.map(mod => {
                if (mod.id === editedMod.id) {
                    return mod;
                }
                return mod;
            });
            if (editedMod?.insert ?? false) {
                console.log('add mod');
                ModsAdd(s_char.value.id,[mod])
            } else {
                console.log('update mod');
                ModsUpdate(s_char.value.id,[mod])
            }
            setEditedMod(null);
            setIsEditing(false)
        }
    };

    const resetChanges = () => {
        if (editedMod) {
            const originalMod = s_char_mods.value.find(mod => mod.id === editedMod.id);
            setEditedMod(originalMod || null);
        }
    };

    const display = (mod: ICharMod) => {
        return <Fragment>
            <Grid item xs={12}>
                {mod.mfrom} {modTitles[mod.what]} {displayMod(mod.mmod)}{mod?.insert ? 'neu erstellt->edit' : null}
                {!mod.disabled ? <Button onClick={() => startEditing(mod)}><EditIcon/></Button> : null}
            </Grid>
        </Fragment>
    }

    const showEdit = (mod: ICharMod) => {
        return <Fragment>
            <Grid item xs={10}>
                <FormControl variant="standard" style={{width: '100%'}}>
                    <TextField label="von"
                               key={"mod-mfrom"}
                               value={editedMod?.mfrom ?? mod.mfrom}
                               onChange={(e) => edit(e)}
                               name={'mfrom'}
                               variant="standard"
                               style={{width: '100%'}}
                    ></TextField>
                </FormControl>
            </Grid>
            <Grid item xs={2}>
                <Button onClick={() => saveChanges()}><SaveAltIcon/></Button>
                <Button onClick={() => resetChanges()}><LockReset/></Button>
                <Button onClick={() => remove(mod.id)}><DeleteIcon/></Button>
            </Grid>

            <Grid item xs={12} md={3}>
                <FormControl variant="standard" style={{width: '100%'}}>
                    <InputLabel id="mod-what">was</InputLabel>
                    <Select labelId={'mod-what'}
                            key={'mod-what'}
                            value={editedMod?.what ?? mod.what}
                            onChange={(e) => edit(e)}
                            name={'what'}
                            style={{width: '100%'}}
                    >
                        <MenuItem value="" disabled={true}><em>None</em></MenuItem>
                        <ListSubheader>Verteidigung</ListSubheader>
                        <MenuItem value={'kv'}>Körperliche Verteidigung</MenuItem>
                        <MenuItem value={'mv'}>Magische Verteidigung</MenuItem>
                        <MenuItem value={'sv'}>Soziale Verteidigung</MenuItem>
                        <ListSubheader>Schwellen</ListSubheader>
                        <MenuItem value={'wund'}>Wundschwelle</MenuItem>
                        <MenuItem value={'bew'}>Bewußtl.schwelle</MenuItem>
                        <MenuItem value={'tot'}>Todesschwelle</MenuItem>
                        <ListSubheader>Rüstung</ListSubheader>
                        <MenuItem value={'pr'}>Physische Rüstung</MenuItem>
                        <MenuItem value={'mr'}>Mystische Rüstung</MenuItem>
                        <ListSubheader>Attribute</ListSubheader>
                        <MenuItem value={'ges'}>Geschicklichkeit</MenuItem>
                        <MenuItem value={'str'}>Stärke</MenuItem>
                        <MenuItem value={'wah'}>Wahrnehmung</MenuItem>
                        <MenuItem value={'zae'}>Zähigkeit</MenuItem>
                        <MenuItem value={'wil'}>Willenskraft</MenuItem>
                        <MenuItem value={'cha'}>Charisma</MenuItem>
                        <ListSubheader>Würfe</ListSubheader>
                        <MenuItem value={'ini'}>Initiativewurf</MenuItem>
                        <MenuItem value={'hit'}>Trefferwurf</MenuItem>
                        <MenuItem value={'dmg'}>Schadenswurf</MenuItem>
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={3} md={3}>
                <FormControl variant="standard" style={{width: '100%'}}>
                    <InputLabel id="mod-mod">Modifikator</InputLabel>
                    <Select labelId="mod-mod"
                            key={'mod-mmod'}
                            value={editedMod?.mmod ?? mod.mmod}
                            onChange={(e) => edit(e)}
                            name={'mmod'}
                            style={{width: '100%'}}
                    >
                        {stepmods.map(r => <MenuItem key={'mmod' + r}
                                                     value={r}>{r > 0 ? '+' + r : r}</MenuItem>)}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={6} md={3}>
                <FormControl variant="standard" style={{width: '100%'}}>
                    <InputLabel id="mod-type">Dauer</InputLabel>
                    <Select
                        labelId="mod-type"
                        key={'mod-modtype'}
                        value={editedMod?.type ?? mod.type}
                        onChange={(e) => edit(e)}
                        name={'type'}
                        style={{width: '100%'}}
                    >
                        {modsTypes.map(r => <MenuItem key={r.key} value={r.key}>{r.name}</MenuItem>)}
                    </Select>
                </FormControl>
            </Grid>
            {modDuration(editedMod?.type || mod.type) ? <Grid item xs={3}>
                <FormControl variant="standard" style={{width: '100%'}}>
                    <InputLabel id="mod-duration">Anzahl</InputLabel>
                    <Select
                        labelId="mod-duration" key={'mod-duration'}
                        value={editedMod?.duration ?? mod.duration}
                        onChange={(e) => edit(e)}
                        name={'duration'}
                        style={{width: '100%'}}
                    >
                        {duration.map(r => <MenuItem key={'mdur' + r} value={r}>{r}</MenuItem>)}
                    </Select>
                </FormControl>
            </Grid> : null}

            {mnum < mods ? <Grid item xs={12}>
                <hr/>
            </Grid> : null}
        </Fragment>
    }

    return <Fragment>
        {!isEditing ? display(mod) : showEdit(mod)}
    </Fragment>
}

export default memo(ModEdit)